/**
 * Security Settings Hooks
 * ISO 27001 A.9.4.2 - Secure log-on procedures
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  securitySettingsApi,
  breachDetectionApi,
  SecuritySettingsUpdate,
} from '../api/security-settings.api';

// Query keys
const SECURITY_SETTINGS_KEY = ['security-settings'];
const SECURITY_SUMMARY_KEY = ['security-settings', 'summary'];
const BREACH_STATUS_KEY = ['breach-detection', 'status'];

/**
 * Hook to fetch security settings
 */
export function useSecuritySettings() {
  return useQuery({
    queryKey: SECURITY_SETTINGS_KEY,
    queryFn: () => securitySettingsApi.getSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch security settings summary
 */
export function useSecuritySettingsSummary() {
  return useQuery({
    queryKey: SECURITY_SUMMARY_KEY,
    queryFn: () => securitySettingsApi.getSummary(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to update security settings
 */
export function useUpdateSecuritySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: SecuritySettingsUpdate) =>
      securitySettingsApi.updateSettings(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SECURITY_SETTINGS_KEY });
      queryClient.invalidateQueries({ queryKey: SECURITY_SUMMARY_KEY });
    },
  });
}

/**
 * Hook to reset security settings to defaults
 */
export function useResetSecuritySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => securitySettingsApi.resetToDefaults(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SECURITY_SETTINGS_KEY });
      queryClient.invalidateQueries({ queryKey: SECURITY_SUMMARY_KEY });
    },
  });
}

/**
 * Hook to get breach detection status
 */
export function useBreachDetectionStatus() {
  return useQuery({
    queryKey: BREACH_STATUS_KEY,
    queryFn: () => breachDetectionApi.getStatus(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to check password breach
 */
export function useCheckPasswordBreach() {
  return useMutation({
    mutationFn: (password: string) => breachDetectionApi.checkPassword(password),
  });
}

/**
 * Hook to check email breach
 */
export function useCheckEmailBreach() {
  return useMutation({
    mutationFn: () => breachDetectionApi.checkEmail(),
  });
}

/**
 * Hook to get all known breaches
 */
export function useAllBreaches() {
  return useQuery({
    queryKey: ['breach-detection', 'all-breaches'],
    queryFn: () => breachDetectionApi.getAllBreaches(),
    staleTime: 60 * 60 * 1000, // 1 hour
    enabled: false, // Only fetch when explicitly requested
  });
}
